import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import {
  Wallet, RefreshCw, CheckCircle2, XCircle, Plus, Trash2,
  Eye, EyeOff, Save, TestTube, BarChart3, ExternalLink, Loader2,
  Link2, Copy, Download, AlertTriangle, Power, Settings2,
  ArrowLeft, ArrowRight, Clock, Filter, Search
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { toast } from "sonner";
import { AdminLayout } from "../../components/AdminLayout";
import adminApi from "../../lib/adminApi";

export default function AdminBlockchainPage() {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [wallets, setWallets] = useState([]);
  const [config, setConfig] = useState({
    enabled: false, network: "mainnet-beta",
    min_balance_enabled: false, min_balance_threshold: 0.01,
  });
  const [stats, setStats] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [generatedKey, setGeneratedKey] = useState(null);

  // Transactions viewer state
  const [txData, setTxData] = useState(null);
  const [txPage, setTxPage] = useState(1);
  const [txFilter, setTxFilter] = useState("all");
  const [txLoading, setTxLoading] = useState(false);

  const loadAll = useCallback(async () => {
    try {
      const [cfgRes, walletsRes, statsRes] = await Promise.all([
        adminApi.get("/admin/blockchain/config"),
        adminApi.get("/admin/blockchain/wallets"),
        adminApi.get("/admin/blockchain/stats"),
      ]);
      setConfig(cfgRes.data);
      setWallets(walletsRes.data);
      setStats(statsRes.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const loadTransactions = useCallback(async (page = 1, filter = "all") => {
    setTxLoading(true);
    try {
      const res = await adminApi.get(`/admin/blockchain/transactions?page=${page}&per_page=15&status=${filter}`);
      setTxData(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setTxLoading(false);
    }
  }, []);

  useEffect(() => { if (config.enabled) loadTransactions(txPage, txFilter); }, [config.enabled, txPage, txFilter, loadTransactions]);

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      const res = await adminApi.put("/admin/blockchain/config", config);
      setConfig(res.data);
      toast.success(t("blockchain.configSaved"));
    } catch (err) {
      toast.error(err.response?.data?.detail || t("blockchain.saveError"));
    } finally {
      setSaving(false);
    }
  };

  const handleActivateWallet = async (walletId) => {
    try {
      await adminApi.put(`/admin/blockchain/wallets/${walletId}`, { is_active: true });
      toast.success(t("blockchain.walletActivated"));
      loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    }
  };

  const handleDeactivateWallet = async (walletId) => {
    try {
      await adminApi.put(`/admin/blockchain/wallets/${walletId}`, { is_active: false });
      toast.success(t("blockchain.walletDeactivated"));
      loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    }
  };

  const handleDeleteWallet = async (walletId) => {
    if (!window.confirm(t("blockchain.confirmDelete"))) return;
    try {
      await adminApi.delete(`/admin/blockchain/wallets/${walletId}`);
      toast.success(t("blockchain.walletDeleted"));
      loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    }
  };

  const handleRefreshBalance = async (walletId) => {
    try {
      const res = await adminApi.get(`/admin/blockchain/wallets/${walletId}/balance`);
      setWallets(prev => prev.map(w =>
        w.id === walletId ? { ...w, balance: res.data.balance } : w
      ));
    } catch (err) {
      toast.error("Error");
    }
  };

  const explorerUrl = (address) => {
    if (config.network === "devnet") return `https://solscan.io/account/${address}?cluster=devnet`;
    return `https://solscan.io/account/${address}`;
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="h-8 w-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
        </div>
      </AdminLayout>
    );
  }

  const activeWallet = wallets.find(w => w.is_active);

  return (
    <AdminLayout>
      <div className="p-4 md:p-8 max-w-5xl" data-testid="admin-blockchain-page">
        {/* Header */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-heading font-bold text-white">{t("blockchain.title")}</h1>
              <p className="text-slate-400 mt-1">{t("blockchain.subtitle")}</p>
            </div>
            {config.enabled ? (
              <span className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-500/20 text-emerald-400 text-sm font-medium" data-testid="blockchain-status-active">
                <CheckCircle2 className="h-4 w-4" /> {t("blockchain.active")}
              </span>
            ) : (
              <span className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-slate-700 text-slate-400 text-sm font-medium" data-testid="blockchain-status-inactive">
                <XCircle className="h-4 w-4" /> {t("blockchain.inactive")}
              </span>
            )}
          </div>
        </motion.div>

        <div className="space-y-6">
          {/* Settings card */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
            className="bg-slate-800 border border-slate-700 rounded-lg p-6">
            <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <Settings2 className="h-5 w-5 text-amber-400" /> {t("blockchain.settings")}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Enable toggle */}
              <div className="flex items-center justify-between col-span-full">
                <Label className="text-slate-300">{t("blockchain.enableBlockchain")}</Label>
                <button onClick={() => setConfig(p => ({ ...p, enabled: !p.enabled }))}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${config.enabled ? "bg-emerald-500" : "bg-slate-600"}`}
                  data-testid="blockchain-toggle">
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${config.enabled ? "translate-x-6" : "translate-x-1"}`} />
                </button>
              </div>
              {/* Network */}
              <div>
                <Label className="text-slate-300 mb-2 block">{t("blockchain.network")}</Label>
                <select value={config.network} onChange={e => setConfig(p => ({ ...p, network: e.target.value }))}
                  className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
                  data-testid="blockchain-network-select">
                  <option value="mainnet-beta">Mainnet Beta</option>
                  <option value="devnet">Devnet</option>
                  <option value="testnet">Testnet</option>
                </select>
              </div>
              {/* Min balance */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-slate-300">{t("blockchain.minBalanceAlert")}</Label>
                  <button onClick={() => setConfig(p => ({ ...p, min_balance_enabled: !p.min_balance_enabled }))}
                    className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${config.min_balance_enabled ? "bg-amber-500" : "bg-slate-600"}`}
                    data-testid="min-balance-toggle">
                    <span className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${config.min_balance_enabled ? "translate-x-5" : "translate-x-1"}`} />
                  </button>
                </div>
                <Input type="number" step="0.001" min="0" value={config.min_balance_threshold}
                  onChange={e => setConfig(p => ({ ...p, min_balance_threshold: parseFloat(e.target.value) || 0 }))}
                  disabled={!config.min_balance_enabled}
                  className="bg-slate-700 border-slate-600 text-white font-mono disabled:opacity-40"
                  data-testid="min-balance-input" />
                <p className="text-xs text-slate-500 mt-1">{t("blockchain.minBalanceHint")}</p>
              </div>
            </div>
            <div className="flex gap-3 pt-4 mt-4 border-t border-slate-700">
              <Button onClick={handleSaveSettings} disabled={saving}
                className="bg-amber-500 hover:bg-amber-600 text-black font-medium" data-testid="blockchain-save-btn">
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                {t("blockchain.save")}
              </Button>
            </div>
          </motion.div>

          {/* Wallets card */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
            className="bg-slate-800 border border-slate-700 rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                <Wallet className="h-5 w-5 text-amber-400" /> {t("blockchain.wallets")}
                <span className="text-sm text-slate-500 font-normal ml-1">({wallets.length})</span>
              </h2>
              <Button onClick={() => setShowAddModal(true)} size="sm"
                className="bg-amber-500 hover:bg-amber-600 text-black font-medium" data-testid="add-wallet-btn">
                <Plus className="h-4 w-4 mr-1" /> {t("blockchain.addWallet")}
              </Button>
            </div>

            {wallets.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <Wallet className="h-10 w-10 mx-auto mb-2 opacity-30" />
                <p>{t("blockchain.noWallets")}</p>
              </div>
            ) : (
              <div className="space-y-3">
                {wallets.map(w => {
                  const belowMin = config.min_balance_enabled && w.balance !== null && w.balance >= 0 && w.balance < config.min_balance_threshold;
                  return (
                    <div key={w.id}
                      className={`border rounded-lg p-4 transition-colors ${
                        w.is_active ? "bg-emerald-950/30 border-emerald-700/60" : "bg-slate-900 border-slate-700"
                      }`} data-testid={`wallet-card-${w.id}`}>
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            {w.is_active && (
                              <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-400 text-xs font-medium">
                                <Power className="h-3 w-3" /> {t("blockchain.active")}
                              </span>
                            )}
                            <span className="font-semibold text-white text-sm">{w.label}</span>
                          </div>
                          <div className="flex items-center gap-2 mt-1">
                            <code className="text-xs font-mono text-amber-400/80 truncate">{w.wallet_address}</code>
                            <a href={explorerUrl(w.wallet_address)} target="_blank" rel="noopener noreferrer"
                              className="text-slate-500 hover:text-amber-400 shrink-0">
                              <ExternalLink className="h-3.5 w-3.5" />
                            </a>
                          </div>
                          {/* Balance */}
                          {w.balance !== null && w.balance !== undefined && (
                            <div className="flex items-center gap-2 mt-2">
                              <span className={`text-lg font-bold font-mono ${belowMin ? "text-red-400" : w.balance >= 0 ? "text-emerald-400" : "text-slate-500"}`}>
                                {w.balance >= 0 ? w.balance.toFixed(6) : "?"} SOL
                              </span>
                              <button onClick={() => handleRefreshBalance(w.id)} className="text-slate-500 hover:text-white p-1">
                                <RefreshCw className="h-3.5 w-3.5" />
                              </button>
                              {belowMin && (
                                <span className="flex items-center gap-1 text-xs text-red-400">
                                  <AlertTriangle className="h-3.5 w-3.5" />
                                  {t("blockchain.belowMinimum")}
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                        {/* Actions */}
                        <div className="flex items-center gap-2 shrink-0">
                          {w.is_active ? (
                            <Button size="sm" variant="outline" onClick={() => handleDeactivateWallet(w.id)}
                              className="border-slate-600 text-slate-300 hover:bg-slate-700 text-xs" data-testid={`deactivate-wallet-${w.id}`}>
                              <XCircle className="h-3.5 w-3.5 mr-1" /> {t("blockchain.deactivate")}
                            </Button>
                          ) : (
                            <>
                              <Button size="sm" onClick={() => handleActivateWallet(w.id)}
                                className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs" data-testid={`activate-wallet-${w.id}`}>
                                <Power className="h-3.5 w-3.5 mr-1" /> {t("blockchain.activate")}
                              </Button>
                              <Button size="sm" variant="outline" onClick={() => handleDeleteWallet(w.id)}
                                className="border-red-800 text-red-400 hover:bg-red-900/30 text-xs" data-testid={`delete-wallet-${w.id}`}>
                                <Trash2 className="h-3.5 w-3.5" />
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </motion.div>

          {/* Stats */}
          {stats && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}
              className="bg-slate-800 border border-slate-700 rounded-lg p-6">
              <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <BarChart3 className="h-5 w-5 text-amber-400" /> {t("blockchain.stats")}
              </h2>
              <div className="grid grid-cols-3 gap-4">
                {[
                  { value: stats.total_votes, label: t("blockchain.totalVotes"), color: "text-white" },
                  { value: stats.blockchain_votes, label: t("blockchain.onChainVotes"), color: "text-emerald-400" },
                  { value: stats.pending_votes, label: t("blockchain.offChainVotes"), color: "text-amber-400" },
                ].map(s => (
                  <div key={s.label} className="bg-slate-900 border border-slate-700 rounded-lg p-4 text-center">
                    <div className={`text-2xl font-bold ${s.color}`}>{s.value}</div>
                    <div className="text-xs text-slate-400 mt-1">{s.label}</div>
                  </div>
                ))}
              </div>
            </motion.div>
          )}

          {/* Transactions Viewer */}
          {config.enabled && txData && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.17 }}
              className="bg-slate-800 border border-slate-700 rounded-lg p-6" data-testid="tx-viewer">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                  <Search className="h-5 w-5 text-amber-400" /> {t("blockchain.txViewer")}
                </h2>
                <Button size="sm" variant="outline" onClick={() => loadTransactions(txPage, txFilter)}
                  className="border-slate-600 text-slate-300 hover:bg-slate-700 text-xs" data-testid="refresh-tx-btn">
                  <RefreshCw className={`h-3.5 w-3.5 mr-1 ${txLoading ? "animate-spin" : ""}`} /> {t("common.refresh")}
                </Button>
              </div>

              {/* Stats bar */}
              {txData.stats && (
                <div className="grid grid-cols-4 gap-3 mb-4">
                  <div className="bg-slate-900 border border-slate-700 rounded-lg p-3 text-center">
                    <div className="text-xl font-bold text-white">{txData.stats.total_votes}</div>
                    <div className="text-xs text-slate-400">{t("blockchain.totalVotes")}</div>
                  </div>
                  <div className="bg-slate-900 border border-slate-700 rounded-lg p-3 text-center">
                    <div className="text-xl font-bold text-emerald-400">{txData.stats.confirmed}</div>
                    <div className="text-xs text-slate-400">{t("blockchain.onChain")}</div>
                  </div>
                  <div className="bg-slate-900 border border-slate-700 rounded-lg p-3 text-center">
                    <div className="text-xl font-bold text-amber-400">{txData.stats.pending}</div>
                    <div className="text-xs text-slate-400">{t("blockchain.pending")}</div>
                  </div>
                  <div className="bg-slate-900 border border-slate-700 rounded-lg p-3 text-center">
                    <div className="text-xl font-bold text-cyan-400">{txData.stats.percentage}%</div>
                    <div className="text-xs text-slate-400">{t("blockchain.coverage")}</div>
                  </div>
                </div>
              )}

              {/* Filter tabs */}
              <div className="flex gap-2 mb-4">
                {[
                  { id: "all", label: t("blockchain.filterAll") },
                  { id: "confirmed", label: t("blockchain.filterConfirmed") },
                  { id: "pending", label: t("blockchain.filterPending") },
                ].map(f => (
                  <button key={f.id} onClick={() => { setTxFilter(f.id); setTxPage(1); }}
                    className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                      txFilter === f.id
                        ? "bg-amber-500 text-black"
                        : "bg-slate-700 text-slate-300 hover:bg-slate-600"
                    }`} data-testid={`tx-filter-${f.id}`}>
                    {f.label}
                  </button>
                ))}
              </div>

              {/* Table */}
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="tx-table">
                  <thead>
                    <tr className="border-b border-slate-700 text-left">
                      <th className="pb-2 text-slate-400 font-medium text-xs">{t("blockchain.colStatus")}</th>
                      <th className="pb-2 text-slate-400 font-medium text-xs">{t("blockchain.colVoteId")}</th>
                      <th className="pb-2 text-slate-400 font-medium text-xs">{t("blockchain.colConsulta")}</th>
                      <th className="pb-2 text-slate-400 font-medium text-xs">{t("blockchain.colTx")}</th>
                      <th className="pb-2 text-slate-400 font-medium text-xs">{t("blockchain.colDate")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {txData.transactions.map((tx, i) => (
                      <tr key={i} className="border-b border-slate-700/50 hover:bg-slate-700/30">
                        <td className="py-2.5">
                          {tx.status === "confirmed" ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-400 text-xs font-medium">
                              <CheckCircle2 className="h-3 w-3" /> On-chain
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-400 text-xs font-medium">
                              <Clock className="h-3 w-3" /> {t("blockchain.pending")}
                            </span>
                          )}
                        </td>
                        <td className="py-2.5 font-mono text-xs text-slate-300">{tx.vote_id}</td>
                        <td className="py-2.5 text-xs text-slate-300 max-w-[180px] truncate">{tx.consulta_title}</td>
                        <td className="py-2.5">
                          {tx.solana_tx ? (
                            <a href={`https://solscan.io/tx/${tx.solana_tx}`} target="_blank" rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 font-mono text-xs text-cyan-400 hover:text-cyan-300">
                              {tx.solana_tx.slice(0, 12)}...
                              <ExternalLink className="h-3 w-3" />
                            </a>
                          ) : (
                            <span className="text-xs text-slate-500">—</span>
                          )}
                        </td>
                        <td className="py-2.5 text-xs text-slate-400">
                          {tx.created_at ? new Date(tx.created_at).toLocaleString("ca") : "—"}
                        </td>
                      </tr>
                    ))}
                    {txData.transactions.length === 0 && (
                      <tr>
                        <td colSpan={5} className="py-8 text-center text-slate-500 text-sm">
                          {t("blockchain.noTransactions")}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {txData.pagination && txData.pagination.total_pages > 1 && (
                <div className="flex items-center justify-between mt-4">
                  <span className="text-xs text-slate-400">
                    {t("blockchain.page")} {txData.pagination.page} / {txData.pagination.total_pages}
                    {" · "}{txData.pagination.total} {t("blockchain.records")}
                  </span>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" disabled={txPage <= 1}
                      onClick={() => setTxPage(p => p - 1)}
                      className="border-slate-600 text-slate-300 hover:bg-slate-700 text-xs" data-testid="tx-prev-page">
                      <ArrowLeft className="h-3.5 w-3.5" />
                    </Button>
                    <Button size="sm" variant="outline" disabled={txPage >= txData.pagination.total_pages}
                      onClick={() => setTxPage(p => p + 1)}
                      className="border-slate-600 text-slate-300 hover:bg-slate-700 text-xs" data-testid="tx-next-page">
                      <ArrowRight className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              )}
            </motion.div>
          )}

          {/* Info */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
            className="bg-slate-800 border border-slate-700 rounded-lg p-5">
            <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
              <Link2 className="h-4 w-4 text-amber-400" /> {t("blockchain.howItWorks")}
            </h3>
            <ul className="text-sm text-slate-400 space-y-1.5 list-disc list-inside">
              <li>{t("blockchain.info1")}</li>
              <li>{t("blockchain.info2")}</li>
              <li>{t("blockchain.info3")}</li>
              <li>{t("blockchain.info4")}</li>
            </ul>
          </motion.div>
        </div>
      </div>

      {/* Add Wallet Modal */}
      <AnimatePresence>
        {showAddModal && (
          <AddWalletModal
            onClose={() => { setShowAddModal(false); setGeneratedKey(null); }}
            onAdded={() => { setShowAddModal(false); setGeneratedKey(null); loadAll(); }}
            generatedKey={generatedKey}
            setGeneratedKey={setGeneratedKey}
            t={t}
          />
        )}
      </AnimatePresence>
    </AdminLayout>
  );
}


function AddWalletModal({ onClose, onAdded, generatedKey, setGeneratedKey, t }) {
  const [mode, setMode] = useState(null); // 'import' | 'generate'
  const [label, setLabel] = useState("");
  const [privateKey, setPrivateKey] = useState("");
  const [showKey, setShowKey] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleImport = async () => {
    if (!privateKey.trim()) return;
    setSubmitting(true);
    try {
      await adminApi.post("/admin/blockchain/wallets", {
        label: label || "Wallet",
        private_key: privateKey.trim(),
      });
      toast.success(t("blockchain.walletImported"));
      onAdded();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleGenerate = async () => {
    setSubmitting(true);
    try {
      const res = await adminApi.post("/admin/blockchain/wallets/generate");
      setGeneratedKey(res.data);
      toast.success(t("blockchain.walletGenerated"));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    } finally {
      setSubmitting(false);
    }
  };

  const copyKey = () => {
    if (generatedKey?.private_key) {
      navigator.clipboard.writeText(generatedKey.private_key);
      toast.success(t("common.copied"));
    }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
        className="bg-slate-800 border border-slate-700 rounded-lg p-6 w-full max-w-lg" onClick={e => e.stopPropagation()}>
        <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <Plus className="h-5 w-5 text-amber-400" /> {t("blockchain.addWallet")}
        </h2>

        {/* Generated key result */}
        {generatedKey && (
          <div className="space-y-3">
            <div className="bg-emerald-950/40 border border-emerald-700/50 rounded-lg p-4">
              <p className="text-sm text-emerald-400 font-medium mb-2 flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4" /> {t("blockchain.walletGenerated")}
              </p>
              <div className="space-y-2">
                <div>
                  <Label className="text-slate-400 text-xs">{t("blockchain.walletAddress")}</Label>
                  <code className="block text-xs font-mono text-amber-400 mt-0.5 break-all">{generatedKey.wallet_address}</code>
                </div>
                <div>
                  <Label className="text-slate-400 text-xs">{t("blockchain.privateKey")}</Label>
                  <div className="flex items-center gap-2 mt-0.5">
                    <code className="flex-1 text-xs font-mono text-red-400 break-all">
                      {showKey ? generatedKey.private_key : "••••••••••••••••••••••"}
                    </code>
                    <button onClick={() => setShowKey(!showKey)} className="text-slate-400 hover:text-white shrink-0">
                      {showKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                    <button onClick={copyKey} className="text-slate-400 hover:text-white shrink-0">
                      <Copy className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
              <p className="text-xs text-red-400 mt-3 flex items-center gap-1">
                <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                {t("blockchain.saveKeyWarning")}
              </p>
            </div>
            <Button onClick={onAdded} className="w-full bg-amber-500 hover:bg-amber-600 text-black font-medium">
              {t("blockchain.done")}
            </Button>
          </div>
        )}

        {/* Mode selection */}
        {!mode && !generatedKey && (
          <div className="grid grid-cols-2 gap-3">
            <button onClick={() => setMode("import")}
              className="bg-slate-900 border border-slate-700 rounded-lg p-4 text-center hover:border-amber-500/50 transition-colors"
              data-testid="import-wallet-btn">
              <Download className="h-6 w-6 mx-auto mb-2 text-amber-400" />
              <span className="text-sm text-white font-medium block">{t("blockchain.importWallet")}</span>
              <span className="text-xs text-slate-500">{t("blockchain.importWalletDesc")}</span>
            </button>
            <button onClick={handleGenerate} disabled={submitting}
              className="bg-slate-900 border border-slate-700 rounded-lg p-4 text-center hover:border-emerald-500/50 transition-colors"
              data-testid="generate-wallet-btn">
              {submitting ? <Loader2 className="h-6 w-6 mx-auto mb-2 text-emerald-400 animate-spin" />
                : <Plus className="h-6 w-6 mx-auto mb-2 text-emerald-400" />}
              <span className="text-sm text-white font-medium block">{t("blockchain.generateWallet")}</span>
              <span className="text-xs text-slate-500">{t("blockchain.generateWalletDesc")}</span>
            </button>
          </div>
        )}

        {/* Import form */}
        {mode === "import" && !generatedKey && (
          <div className="space-y-4">
            <div>
              <Label className="text-slate-300 mb-2 block">{t("blockchain.walletLabel")}</Label>
              <Input value={label} onChange={e => setLabel(e.target.value)} placeholder="Ex: Wallet Votacions 2026"
                className="bg-slate-700 border-slate-600 text-white" data-testid="wallet-label-input" />
            </div>
            <div>
              <Label className="text-slate-300 mb-2 block">{t("blockchain.privateKey")}</Label>
              <div className="relative">
                <Input type={showKey ? "text" : "password"} value={privateKey}
                  onChange={e => setPrivateKey(e.target.value)}
                  placeholder={t("blockchain.enterPrivateKey")}
                  className="bg-slate-700 border-slate-600 text-white pr-10 font-mono text-sm"
                  data-testid="import-private-key-input" />
                <button type="button" onClick={() => setShowKey(!showKey)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white">
                  {showKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <div className="flex gap-3">
              <Button onClick={handleImport} disabled={submitting || !privateKey.trim()}
                className="flex-1 bg-amber-500 hover:bg-amber-600 text-black font-medium" data-testid="confirm-import-btn">
                {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Download className="h-4 w-4 mr-2" />}
                {t("blockchain.importWallet")}
              </Button>
              <Button onClick={() => setMode(null)} variant="outline" className="border-slate-600 text-slate-300 hover:bg-slate-700">
                {t("common.cancel")}
              </Button>
            </div>
          </div>
        )}

        {!mode && !generatedKey && (
          <div className="mt-4 text-right">
            <Button onClick={onClose} variant="outline" className="border-slate-600 text-slate-300 hover:bg-slate-700">
              {t("common.cancel")}
            </Button>
          </div>
        )}
      </motion.div>
    </motion.div>
  );
}
